/**
 * A aba da mesa única pelo endereço (E205.F1.T02, ADR-0147 §1). Substitui o `useAncoraDaMesa` da
 * E201.F1, que só rolava até a área.
 *
 * - A tabela de `abaDaAncora` escolhe a aba no próprio render: o próximo passo (`/mesa#conclusao`),
 *   o "Alterar na mesa" do documento e os links antigos chegam à aba certa sem piscar a anterior.
 * - A âncora de um bloco (o campo do banco, a seção do documento, o envio) rola até ele depois que a
 *   aba dele está à vista; a busca se repete por alguns instantes, porque o bloco aparece quando os
 *   dados chegam. A âncora fora da tabela acha a aba pelo painel que contém o bloco.
 * - O nome da aba no endereço não rola a página: as abas ficam onde estão.
 * - Clicar numa aba grava o hash (`replace`), mantendo `?saida=` e `?trecho=`.
 */
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ABA_PADRAO, abaDaAncora, abaDoElemento, eAba, type AbaDaMesa } from '@/lib/mesaEmAbas';

const TENTATIVAS = 20;
const INTERVALO_MS = 150;

export interface AbaDaMesaNoEndereco {
  /** A âncora do endereço, sem o `#`. */
  ancora: string | null;
  aba: AbaDaMesa;
  escolher: (aba: AbaDaMesa) => void;
}

function useRolagemAteOBloco(
  id: string | null,
  key: string,
  semTabela: boolean,
  onAbaDoBloco: (aba: AbaDaMesa) => void,
): void {
  useEffect(() => {
    if (id === null || eAba(id)) return undefined;
    let tentativas = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tentar = (): void => {
      const alvo = document.getElementById(id);
      if (alvo !== null && alvo.closest('[hidden]') === null) {
        // `scrollIntoView` é opcional porque o jsdom não o implementa.
        alvo.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
        return;
      }
      const daPagina = semTabela ? abaDoElemento(alvo) : null;
      if (daPagina !== null) onAbaDoBloco(daPagina);
      tentativas += 1;
      if (tentativas < TENTATIVAS) timer = setTimeout(tentar, INTERVALO_MS);
    };
    tentar();
    return () => {
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [id, key, semTabela, onAbaDoBloco]);
}

export function useAbaDaMesa(): AbaDaMesaNoEndereco {
  const { pathname, search, hash, key } = useLocation();
  const navigate = useNavigate();
  const ancora = hash.length > 1 ? decodeURIComponent(hash.slice(1)) : null;
  const daTabela = abaDaAncora(ancora);
  // A última aba escolhida: a âncora fora da tabela não troca de aba até achar o bloco.
  const [ultima, setUltima] = useState<AbaDaMesa>(daTabela ?? ABA_PADRAO);
  if (daTabela !== null && daTabela !== ultima) setUltima(daTabela);
  useRolagemAteOBloco(ancora, key, daTabela === null, setUltima);

  const escolher = useCallback(
    (aba: AbaDaMesa) => navigate({ pathname, search, hash: aba }, { replace: true }),
    [navigate, pathname, search],
  );
  return { ancora, aba: daTabela ?? ultima, escolher };
}
